import { Application, FeathersService, Query, Service } from "@feathersjs/feathers";
import { action, computed, makeObservable, observable, reaction, when } from "mobx";
import client from "src/feathers";
import { DataType, IItemItem, IItemLink, ItemType, Services } from "src/types";
import { RootStore, BaseDataStore } from ".";
import { Item } from "../models";
import { Filter, IItem } from "../types";
import { paramsForServer } from "feathers-hooks-common";
import toast from "react-hot-toast";
import i18next from "src/i18n";
import { DataContainer } from "./dataContainer";
import { EventInput } from "@fullcalendar/react";
import { resetTime } from "src/util/util";

import { theme } from "src/config";

export class ItemStore<T extends Item & I, I extends IItem = IItem, C extends string = string> extends BaseDataStore<T, I, C> {
  public    readonly archivePageSize: number = 50; // How many items are loaded for the archive
  protected readonly overviewLimit:   number = 10; // How many items are loaded for the overview sections
  protected readonly item_link_service: FeathersService<Application<IItemItem>, Service<IItemItem>> = client.service(Services.ItemItem);
  protected page: number = 0;

  query:         Query   = {};
  searchString:  string  = "";
  searchFilter:  Filter  = {};
  advSearch:     boolean = false;
  links:         DataContainer<IItemLink, IItemLink> = null;
  linkOptions:   DataContainer<I, T>     = null;
  archive:       DataContainer<I, T>     = null;
  filterProps:   (keyof T | string)[]    = [];
  filterValues:  {[prop: string]: any[]} = {};
  defaultFilter: {[prop: string]: any[]} = {}; // Default filter values that are applied when entering a room
  isDefaultFilter: boolean = false;

  constructor(readonly rootStore: RootStore, protected readonly create: (data?: I) => T, protected readonly data_type: DataType, protected readonly linkTypes: DataType[], registerEventListener: boolean = true) {
    super(rootStore, create, data_type, registerEventListener);

    makeObservable(this, {
      query:           observable,
      searchString:    observable,
      searchFilter:    observable,
      filterValues:    observable,
      advSearch:       observable,
      isDefaultFilter: observable,
      
      getQueries:      computed,

      load:             action,
      resetPagination:  action,
      setSearchString:  action,
      updateQuery:      action,
      setFilterValue:   action,
      toggleAdvSearch:  action,
      resetFilterVales: action,
    });

    this.eventCallBacks.after.created.push(this.filterRoom, this.filterArchived);
    this.eventCallBacks.after.removed.push(this.filterRoom, this.filterArchived);
    this.eventCallBacks.after.updated.push(this.filterRoom, this.filterArchived);
    this.eventCallBacks.after.patched.push(this.filterRoom, this.filterArchived);

    // Prevent error output to the console when hook chain is canceled
    this.eventCallBacks.error.all.push(() => {});
  }

  /*******************************************************************************************************************************************
   * Event Hooks
   ********************************************************************************************************************************************/
  /**
   * Filter incoming updates by room_id. While channels are setup to
   * avoid events of items outside the room the user is in, 
   * to support dashboard events items where the user is set to from
   * or to are still received. 
   */
  private filterRoom = (data: I): void => {
    if(!this.rootStore.spaceStore.onDashboard) {
      if(Number(data.room_id) !== this.rootStore.authStore.getCurRoomID) 
        throw new Error("Received question from other room");
    }
  }

  /**
   * Filter out archived items
   */
  private filterArchived = (data: I): void => {
    if(data.archived) delete this.container[data.id];
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();
    
    // Load items when the selected filter changes
    reaction(() => this.query, () => {
      if(this.rootStore.authStore.isLoggedIn) {
        this.resetPagination();
        this.load(true);
      }
    }, {name: `${this.data_type}_filter_load`});

    // One-shot when is needed for loggedin pageload to dashboard
    when(() => this.rootStore.spaceStore.onDashboard, () => this.loadDashboard());
    when(() => this.rootStore.spaceStore.inRoom, () => this.loadRoom());

    // Load and discard rooms and dashboard
    reaction(() => [this.rootStore.spaceStore.onDashboard], () => {
      if(this.rootStore.spaceStore.onDashboard) {
        this.loadDashboard();
      } else {
        this.clearDashboard();
      }
    }, {name: `${this.data_type}_dashboard_load`});
    reaction(() => [this.rootStore.authStore.getCurRoomID, this.rootStore.spaceStore.inRoom],  () => { // enter room
      if(this.rootStore.spaceStore.inRoom) this.loadRoom();
    }, {name: `${this.data_type}_room_load`});

    // Load and discard links for the active item
    reaction(() => this.rootStore.getActiveItem, () => {
      if(this.rootStore.getActiveItem) {
        this.links.load();
      } else {
        this.links.clear();
        if(this.data_type !== DataType.Deliverable) {
          this.linkOptions.clear();
        }
      };
    }, {name: `${this.data_type}_active_loadLinks`});

    // Change Workspace:
    reaction(() => this.rootStore.authStore.getCurWorkspaceID, () => {
      this.clear();
      this.loadDashboard();
    })

    this.setupStorage();
  }

  protected setupStorage() {
    // Item links of the active item to other items of the type of this store
    this.links = new DataContainer<IItemLink, IItemLink>(
      null,
      this.item_link_service,
      {
        paginate:   false,
        limited:    false,
        sortProp:   "id",
        conversion: Item.parseLink,
        eventFilter: (link: IItemLink) => !link.archived,
        name:       this.data_type + ".links",
      }
    );
    this.item_link_service.on("removed", (data: IItemItem) => this.updateItemLink(data, "removed"));
    this.item_link_service.on("patched",   (data: IItemItem) => this.updateItemLink(data, "updated"));
    this.item_link_service.on("updated",  (data: IItemItem) => this.updateItemLink(data, "updated"));
    this.item_link_service.on("created", (data: IItemItem) => this.updateItemLink(data, "created"));
    reaction(() => this.rootStore.getActiveItem && this.rootStore.getActiveItem.id, () => {
      if(!this.rootStore.getActiveItem?.id) return;

      this.links.setQuery(this.rootStore.getActiveItem ? paramsForServer({
          item_id: this.rootStore.getActiveItem.id,
          query: {
          $or:   [
            { item1_id: this.rootStore.getActiveItem.id, data_type2: this.data_type},
            { item2_id: this.rootStore.getActiveItem.id, data_type1: this.data_type},
        ]}}) : null
      )
    });

    // Items of the type of this store that can be linked to the currently active item
    this.linkOptions = new DataContainer<I, T>(
      { room_id: this.rootStore.authStore.getCurRoomID },
      this.service,
      {
        paginate:    true,
        limited:     false,
        pageSize:    10,
        conversion:  this.create,
        eventFilter: (item: I) => !item.archived,
        name:        this.data_type + ".linkOptions",
      }
    );
    reaction(() => this.rootStore.getActiveItem, () => {
      if(this.rootStore.spaceStore.onDashboard) return;

      const query: Query = { room_id: this.rootStore.authStore.getCurRoomID };
      if(this.getActive?.id ) query["id"] = {$ne: this.getActive.id};
      this.linkOptions.setQuery(query);
    });

    // Setup archive container
    this.archive = new DataContainer<I, T>(
      {room_id: this.rootStore.authStore.getCurRoomID, archived: true},
      this.service,
      {
        paginate:    true,
        limited:     true,
        extendPage:  false,
        pageSize:    this.archivePageSize,
        conversion:  this.create,
        eventFilter: (item: I) => item.archived,
        name:        this.data_type + ".archive",
      }
    );
    this.archive.addEventCallbacks(this.eventCallBacks);
    reaction(() => this.rootStore.authStore.getCurRoomID, () => {
      this.rootStore.authStore.getCurRoomID && this.archive.setQuery({
        room_id: this.rootStore.authStore.getCurRoomID, archived: true
      })
    });
  };

  protected dashboardEventFilter(item: I): boolean {
    return true;
  }

  /**
   * Called by updated and patched event listener. Checks if the updated 
   * item is set as active item. Desc and title are excluded from the realtime updates while they are edited.
   */
  protected updateActive = (data: I): void => {
    if(this.getActive && this.getActive.id === Number(data.id)) {
      const patch: I = {...data};
      if(this.getActive.getEditProp === "desc" && this.getActive.desc !== data.desc)  {
        delete patch.desc;
        this.getActive.setConflict(data.desc);
        toast.error(i18next.t("item_changed"));
      }
      if(this.getActive.getEditProp === "title" && this.getActive.title !== data.title)  {
        delete patch.title;
        this.getActive.setConflict(data.title);
        toast.error(i18next.t("item_changed"));
      }
      this.getActive.patch(patch);
    }
  }
  protected getCurFilter = (): Query => {
    return this.query;
  };

  /*******************************************************************************************************************************************
   * Database Access 
   ********************************************************************************************************************************************/

  /**
  * Add item_item link to the database.
  */
  addItemLink = async (item_id: number, data_type: ItemType) => {
    if(!this.getActive || this.rootStore.itemStores[data_type].links.getItems[item_id]) return;

    const link: IItemItem = {
      item1_id:     this.getActive.id,
      item2_id:     item_id,
      data_type1:   this.data_type,
      data_type2:   data_type,
      room_id:      this.getActive.room_id,
      workspace_id: this.getActive.workspace_id,
    }
    try {
      await this.item_link_service.create(link);
    } catch (err) {
      console.error("Failed to create link", err);
    }
  }

  /**
   * Remove item_item link from the database.
   */
  removeItemLink = async (link_id: number, type: ItemType) => {
    const link = this.rootStore.itemStores[type].links.getArray.find(l => Number(l.id) === Number(link_id));
    if(!link) return;

    try {
      await this.item_link_service.remove(link.link_id);
    } catch (err) {
      console.error("Failed to remove link", err);
    }
  }

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  get getQueries(): {[name: string]: Query} {
    return {}
  }

  get getCalendarEvents(): EventInput[] {
    return Object.entries(
      this.getItems
        .filter(i => !this.rootStore.spaceStore.projectFilter.length || 
          this.rootStore.spaceStore.projectFilterHasProject(i.getProjectID)
        )
        .reduce((res: {[date: string]: {count: number, items: T[]}}, val) => {
          if(!val.date) return res;
          const key: Date = resetTime(val.date);
          res[key.toISOString()] = res[key.toISOString()] || {count: 0, items: []};
          res[key.toISOString()].count++;
          res[key.toISOString()].items.push(val);
          return res;
        }, {})).map(([key, value]) => ({
          id:        this.data_type,
          groupId:   this.data_type,
          title:     ""+value.count,
          color:     `${theme.color.items[(this.data_type as ItemType)]}`,
          start:     new Date(key),
          editable:  false,
          extendedProps: {
            items: value.items,
          }
        }));
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  /**
   * Load items according to current set filter. If pagination is enabled, 
   * page number is handled automatically and repeated calls of load will load 
   * subsequent pages until resetPagination() is called.
   * 
   * @param paginate 
   */
  load = (paginate: boolean = false): void => {
    if(!this.rootStore.authStore.isLoggedIn) return;
    this.loadItems(this.getCurFilter(), paginate, paginate ? this.page : 0);
    this.page = paginate ? this.page+1 : 0;
  }

  resetPagination = (): void => { this.page = 0;}

  /**
   * Set filter to load items for the current room
   */
  loadRoom() {
    this.isDefaultFilter = false;
    this.resetFilterVales();
    this.archive.load();
  }

  /**
   * Set filter to load items for the dashboard and loads storage
   */
  loadDashboard() {
    this.archive.clear();
  }

  /**
   * Clear storage from dashboard
   */
  clearDashboard = () => {}

  /**
   * Update item link on display. Only active items have to be considered here 
   * since items only load their links when they are set as active.
   */
  private updateItemLink = async (data: IItemItem, method: "created" | "removed" | "updated") => {
    if(!this.rootStore.getActiveItem ||
      !((Number(data.item1_id) === this.rootStore.getActiveItem.id && data.data_type2 === this.data_type) || 
        (Number(data.item2_id) === this.rootStore.getActiveItem.id && data.data_type1 === this.data_type) )
    ) return;
    if(method === "created") {
      const newLink: IItemLink = Item.parseLink(await this.item_link_service.get(data.id, paramsForServer({item_id: this.rootStore.getActiveItem.id})) as unknown as IItemLink);
      this.links.update(newLink, "created")
    } else if(method === "removed"){
      const item_id = Number(data.item1_id) === this.rootStore.getActiveItem.id ? data.item2_id : data.item1_id;
      this.links.update({id: item_id} as IItemLink, "removed");
    } else {
      if(data.archived) {
        const item_id = Number(data.item1_id) === this.rootStore.getActiveItem.id ? data.item2_id : data.item1_id;
        this.links.update({id: item_id} as IItemLink, "removed");
      }
    }
  }

  /**
   * In combination with rootStore.itemStores this allows generic creation of new items.
   */
  createNew = () => {
    return this.create();
  }

  /**
   * @param val    New search string
   * @param update if true, the container query is also updated to trigger a reload
   */
  setSearchString = (val: string, update?: boolean) => {
    this.searchString = val;
    if((!val || val) === "" && update) this.loadRoom();
    else if(update) this.updateQuery();
  }

  /**
   * Update the query that is used for the main container based on filter settings.
   * This will trigger a reload to update the container items. 
   * 
   * The current filter settings are stored in 2 members:
   * the array @filterProps which stores a string list of props which are filtered by 
   * and the object @filterValues which holds the values that the strings in @filterProps can take.
   * 
   * Note, that filterValues is set directly from multiselects. As such, the array contains objects of type Option
   */
  updateQuery = () => {
    this.isDefaultFilter = false;
    this.query = {
      ...this.query,
      // Title and Desc are taken care of separately for the search string
      $or: [
        {[this.data_type + ".title"]: {$ilike: `%${this.searchString}%`}},
        {[this.data_type + ".desc"]:  {$ilike: `%${this.searchString}%`}},
      ],
      // Next add all the selected filter props:
      ...this.filterProps.reduce((res: {[prop: string]: any}, prop) => {
        if(this.filterValues[prop as string] === undefined && !this.filterValues.attributes?.map(f => f.key).includes(prop)) return res;
        if(prop === "date" && this.filterValues[prop as string]) res[this.data_type + "." + (prop as string)] = {
          $gte: this.filterValues[prop as string][0],
          $lte: this.filterValues[prop as string][1],
        };
        else if(this.filterValues.attributes?.map(f => f.key).includes(prop)) {
          if(prop === "answered") res[this.data_type + "." + (prop as string)] = {$ne: null};
          else res[this.data_type + "." + (prop as string)] = true;
        }
        else res[this.data_type + "." + (prop as string)] = {$in: this.filterValues[prop as string].map(f => f.key)};
        return res;
      }, {})
    }
    if(!this.searchString || this.searchString === "") delete this.query["$or"];

    for(const prop of this.filterProps) {
      if(
        (!this.filterValues[prop as string] || !Object.values(this.filterValues[prop as string]).length)
        && !this.filterValues.attributes?.map(f => f.key).includes(prop)
      ) {

        delete this.filterValues[prop as string];
        delete this.query[this.data_type + "." + (prop as string)];
      }
    }
  }

  /**
   * Set a filter that will be used in the next load for the main container.
   * Possible props are given by @filterProp and values should always be lists. 
   * List values are combined via logical OR and different props with logical AND
   * in the final query. 
   * 
   * @param prop  Prop that must be part of @filterProps
   * @param value List of values. Use undefined if this prop should not be used in the filter.
   * @param reset If true, the filter is completely reset before the new value is set. 
   */
  setFilterValue = (prop: keyof T | string, value: any[], reset?: boolean) => {
    this.isDefaultFilter = false;
    if(reset) this.filterValues = {};
    if(this.filterValues[prop as string] === value) return;
    if(!value || !value.length) delete this.filterValues[prop as string];
    else this.filterValues[prop as string] = value;
  }

  /**
   * Reset room filter values back to default, i.e. board filters
   */
  resetFilterVales = (): void => {
    if(this.isDefaultFilter) return;
    this.filterValues    = this.defaultFilter;
    this.query           = {[this.data_type + ".room_id"]: this.rootStore.authStore.getCurRoomID};
    this.updateQuery();
    this.isDefaultFilter = true;
  }

  /**
   * Toggles advanced search modus on and off. This is set by the search bar button and
   * used to show/hide position filters vs advanced filters.
   */
  toggleAdvSearch = () => {
    this.advSearch = !this.advSearch;
    if(!this.advSearch) this.loadRoom();
  }
}
